import { Modal } from 'common/ui/modal';
import { AggPageFormContext, Faqs, Pros } from 'context/page-form-context';
import { aiVariantTypeMap, melFieldTitleMap } from 'modules/mel-ai/constants';
import { useMelContext } from 'modules/mel-ai/contexts';
import { useMelContextForm } from 'modules/mel-ai/hooks';
import {
  AiVariantCommentMap,
  AiVariantType,
  MelFields,
  RequestContextHistoryBody,
} from 'modules/mel-ai/types';
import { mapContextHistory } from 'modules/mel-ai/utils';
import { useContext, useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { melApiSlice } from 'store/api/mel-api/mel-api';

import { ContentMap } from './content-map';
import styles from './mel-chat-modal.module.scss';
import { MelChatModalActions } from './mel-chat-modal-actions';
import { VariantMap } from './variant-map';

interface MelChatModalProps {
  currentFieldName: MelFields;
  onApply: () => void;
  onClose: () => void;
  open: boolean;
  originalContent: string | Faqs | Pros;
}

const MelChatModal = ({
  currentFieldName,
  onApply,
  onClose,
  open,
  originalContent,
}: MelChatModalProps) => {
  const isFaqs = currentFieldName === MelFields.Faqs;
  const isPros = currentFieldName === MelFields.Pros;
  const isSummary = currentFieldName === MelFields.Summary;
  const isString = typeof originalContent === 'string' && !isPros;
  const type = aiVariantTypeMap[currentFieldName];
  const disabledContext = isFaqs || isPros;

  const { pageId } = useContext(AggPageFormContext);
  const { currentProduct, handleRequestVariant, selectedVariant, setSelectedVariant, variantsMap } =
    useMelContext();
  const form = useMelContextForm();
  const { useFetchContextHistoryMutation } = melApiSlice;

  const [fetchContextHistory, fetchContextHistoryResult] = useFetchContextHistoryMutation();

  const mappedContextHistory = useMemo<AiVariantCommentMap>(
    () =>
      mapContextHistory(
        fetchContextHistoryResult.data,
        type === AiVariantType.Page
          ? pageId
          : type === AiVariantType.Product && currentProduct?.id
          ? currentProduct?.id
          : undefined,
      ),
    [fetchContextHistoryResult.data],
  );

  useEffect(() => {
    const data: RequestContextHistoryBody = {
      type,
    };

    fetchContextHistory(data);
  }, []);

  return (
    <Modal className={styles.modal} open={open}>
      <FormProvider {...form}>
        <Modal.Title className={styles.title}>
          AI Variants {`"${melFieldTitleMap[currentFieldName]}"`}
        </Modal.Title>
        <Modal.Body className={styles.body}>
          {!disabledContext && (
            <div className={styles.contentBox}>
              <ContentMap
                contextHistory={mappedContextHistory[currentFieldName] ?? []}
                form={form}
                isFaqs={isFaqs}
                isPros={isPros}
                isString={isString}
                originalContent={originalContent}
              />
            </div>
          )}
          <div className={styles[`variants${disabledContext ? '__fullWidth' : ''}`]}>
            <VariantMap
              currentFieldName={currentFieldName}
              isFaqs={isFaqs}
              isPros={isPros}
              isString={isString}
              selectedVariantState={[selectedVariant, setSelectedVariant]}
              variantsMap={variantsMap}
            />
          </div>
        </Modal.Body>
        <Modal.Actions className={styles.actions}>
          <MelChatModalActions
            disableContext={disabledContext}
            form={form}
            handleRequestVariant={handleRequestVariant}
            isFaqs={isFaqs}
            isSummary={isSummary}
            onApply={onApply}
            onClose={onClose}
            originalContent={originalContent}
            selectedVariant={selectedVariant}
          />
        </Modal.Actions>
      </FormProvider>
    </Modal>
  );
};

export { MelChatModal };
